import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import QRCode from "react-qr-code";
import { db } from "../../services/firebase";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Paper,
  TablePagination,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import HeightIcon from "@mui/icons-material/Height";
import AddIcon from "@mui/icons-material/Add";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import {
  formatDate,
  StyledTableCell,
  StyledTableRow,
} from "../../data/contents/styles";
import LeftPannel from "../components/LeftPannel";
import HeaderPannel from "../components/HeaderPannel";
import RegisterModal from "./RegisterUsers";

const AdminDashboard = () => {
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [sortOrder, setSortOrder] = useState("desc"); // Changed from "default" to "asc"
  const [sortField, setSortField] = useState("addedon");
  const [imageModal, setImageModal] = useState({
    open: false,
    name: "",
    id: "",
  });

  const [openRegisterModal, setOpenRegisterModal] = useState(false);

  // Modify the useEffect to sort data on initial load
  const fetchAndSortCategories = async () => {
    const querySnapshot = await getDocs(collection(db, "excel"));
    const cats = [];
    querySnapshot.forEach((doc) => {
      cats.push({ docid: doc.id, ...doc.data() });
    });

    // Sort by addedon in descending order (newest first)
    const sortedCats = [...cats].sort((a, b) => {
      // Convert to numbers for comparison
      const timeA = Number(a.addedon);
      const timeB = Number(b.addedon);
      // Sort in descending order (newest first)
      return timeB - timeA;
    });

    setCategories(sortedCats);
  };

  useEffect(() => {
    fetchAndSortCategories();
  }, []);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field) => {
    let newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    if (field !== sortField) {
      newSortOrder = "asc";
    }
    setSortField(field);
    setSortOrder(newSortOrder);

    const sortedCategories = [...categories];
    sortedCategories.sort((a, b) => {
      if (field === "addedon") {
        return newSortOrder === "asc"
          ? Number(a.addedon) - Number(b.addedon)
          : Number(b.addedon) - Number(a.addedon);
      }
      if (field === "food" || field === "kit") {
        return newSortOrder === "asc"
          ? a[field] === b[field]
            ? 0
            : a[field]
            ? -1
            : 1
          : a[field] === b[field]
          ? 0
          : a[field]
          ? 1
          : -1;
      }
      return newSortOrder === "asc"
        ? (a[field] || "").localeCompare(b[field] || "")
        : (b[field] || "").localeCompare(a[field] || "");
    });
    setCategories(sortedCategories);
  };

  // Add image modal handlers
  const handleImageClick = (name, id) => {
    setImageModal({ open: true, name, id });
  };

  const handleCloseModal = () => {
    setImageModal({ open: false, name: "", id: "" });
  };

  const handleDownloadImage = (name) => {
    const svg = document.getElementById("QRCode");
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `${name}_qr.png`;
      downloadLink.href = `${pngFile}`;
      downloadLink.click();
    };
    img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
  };

  // Helper: render Yes/No badge with colors for booleans
  const BoolBadge = ({ value }) => {
    const text = value ? "Yes" : "No";
    const color = value ? "green" : "red";
    return <span style={{ color, fontWeight: "bold" }}>{text}</span>;
  };

  return (
    <Box>
      {/* Mobile view warning */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          width: "100vw",
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
          fontWeight: "bold",
        }}
      >
        Not compatible with small screens. Please use desktop or laptop.
      </Box>

      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          backgroundColor: "white",
          display: { xs: "none", sm: "none", md: "flex" },
        }}
      >
        {/* left panel */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            width: "18vw",
            mt: 1.5,
            pl: 0.5,
          }}
        >
          <LeftPannel />
        </Box>
        <Box sx={{ minWidth: "calc( 99vw - 18vw)", ml: 1.5 }}>
          <HeaderPannel HeaderTitle="Admin Dashboard" />

          <div style={{ padding: "5px 15px 10px 5px" }}>
            {/* Table */}
            <RegisterModal
              openRegisterModal={openRegisterModal}
              setOpenRegisterModal={setOpenRegisterModal}
              fetchAndSortCategories={fetchAndSortCategories}
            />
            <Button
              variant="outlined"
              color="primary"
              sx={{ mb: 1, fontWeight: "bold" }}
              startIcon={<AddIcon />}
              onClick={() => setOpenRegisterModal(true)}
            >
              {" "}
              Register Member
            </Button>

            <TableContainer
              component={Paper}
              sx={{ maxHeight: "77vh", overflowY: "auto" }}
            >
              <Table
                stickyHeader
                sx={{ minWidth: 650 }}
                aria-label="categories table"
              >
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell
                      onClick={() => handleSort("addedon")}
                      sx={{
                        cursor: "pointer",
                        "& .content": {
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          width: "100%",
                        },
                      }}
                    >
                      <div className="content">
                        Added On
                        {sortField === "addedon" ? (
                          sortOrder === "asc" ? (
                            <UpgradeIcon />
                          ) : (
                            <VerticalAlignBottomIcon />
                          )
                        ) : (
                          <HeightIcon /> // Removed sortOrder !== "default" condition
                        )}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell
                      onClick={() => handleSort("name")}
                      sx={{
                        cursor: "pointer",
                        "& .content": {
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          width: "100%",
                        },
                      }}
                    >
                      <div className="content">
                        Name
                        {sortField === "name" ? (
                          sortOrder === "asc" ? (
                            <UpgradeIcon />
                          ) : (
                            <VerticalAlignBottomIcon />
                          )
                        ) : (
                          sortOrder !== "default" && <HeightIcon />
                        )}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell>Phone</StyledTableCell>
                    <StyledTableCell>Image</StyledTableCell>
                    <StyledTableCell>ID</StyledTableCell>
                    <StyledTableCell
                      onClick={() => handleSort("food")}
                      sx={{
                        cursor: "pointer",
                        "& .content": {
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          width: "100%",
                        },
                      }}
                    >
                      <div className="content">
                        Food
                        {sortField === "food" ? (
                          sortOrder === "asc" ? (
                            <UpgradeIcon />
                          ) : (
                            <VerticalAlignBottomIcon />
                          )
                        ) : (
                          sortOrder !== "default" && <HeightIcon />
                        )}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell
                      onClick={() => handleSort("kit")}
                      sx={{
                        cursor: "pointer",
                        "& .content": {
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          width: "100%",
                        },
                      }}
                    >
                      <div className="content">
                        Kit
                        {sortField === "kit" ? (
                          sortOrder === "asc" ? (
                            <UpgradeIcon />
                          ) : (
                            <VerticalAlignBottomIcon />
                          )
                        ) : (
                          sortOrder !== "default" && <HeightIcon />
                        )}
                      </div>
                    </StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {categories
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((cat) => (
                      <StyledTableRow key={cat.docid}>
                        <StyledTableCell>
                          {formatDate(cat.addedon)}
                        </StyledTableCell>

                        <StyledTableCell>{cat.name || ""}</StyledTableCell>
                        <StyledTableCell>{cat.phone || ""}</StyledTableCell>
                        <StyledTableCell>
                          {cat.url ? (
                            <img
                              src={cat.url}
                              alt={cat.name}
                              style={{
                                width: 60,
                                height: 40,
                                objectFit: "cover",
                                cursor: "pointer",
                              }}
                              onClick={() => handleImageClick(cat.name, cat.id)}
                            />
                          ) : (
                            <div
                              style={{
                                width: 60,
                                height: 40,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                              onClick={() => handleImageClick(cat.name, cat.id)}
                            >
                              <QRCode
                                value={cat.id || ""}
                                size={50}
                                bgColor="transparent"
                                fgColor="#000000"
                              />
                            </div>
                          )}
                        </StyledTableCell>
                        <StyledTableCell>{cat.id || ""}</StyledTableCell>

                        <StyledTableCell>
                          <BoolBadge value={!!cat.food} />
                        </StyledTableCell>

                        <StyledTableCell>
                          <BoolBadge value={!!cat.kit} />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                </TableBody>
              </Table>
              <Box
                sx={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 3,
                  backgroundColor: "background.paper",
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  component="div"
                  count={categories.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            </TableContainer>
          </div>
        </Box>
      </Box>

      <Dialog open={imageModal.open} onClose={handleCloseModal} maxWidth="md">
        <DialogTitle>{imageModal.name}</DialogTitle>
        <DialogContent>
          <QRCode
            id="QRCode"
            value={imageModal.id}
            size={256}
            bgColor="white"
            fgColor="#000000"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="outlined">
            Close
          </Button>
          <Button
            onClick={() => handleDownloadImage(imageModal.name)}
            color="primary"
            variant="contained"
          >
            Download QR
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
